import { MouseEvent, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { FaSteam } from "react-icons/fa";

import botAvatar from "MVVM/Assets/Steambot/allstar-bot-avatar.jpeg";
import { analytics } from "MVVM/Analytics";
import { useClipSettingsModal } from "MVVM/Hooks/useClipQualitySettingsModal";
import { useSteambot } from "MVVM/Hooks/useSteambot";
import { useUser } from "MVVM/Hooks/useUser";
import { URLS } from "MVVM/Utilities/Maps";
import {
  Actions,
  Footnote,
  Identity,
  Name,
  PrimaryButton,
  PrimaryLink,
  Row,
  SecondaryButton,
  Status,
  TextButton,
  Tone,
} from "./styledComponents";

// Shared by the settings tab, /setup, /add-steam-bot and the bubble modal:
// request a bot, add it on Steam, poll until the friendship lands.
export const SteambotEnroll = () => {
  const history = useHistory();
  const { allstarUser } = useUser();
  const { open: openClipSettings } = useClipSettingsModal();
  const {
    loading,
    status,
    enrolled,
    assignedBot,
    assigning,
    requestAssignment,
  } = useSteambot();
  const requested = useRef(false);
  // Steam can't tell us a profile was opened, so remember the click.
  const [opened, setOpened] = useState(false);

  // Assignment isn't a reservation, it only picks which bot to show, so ask
  // as soon as the view opens.
  useEffect(() => {
    if (requested.current) return;
    if (
      !status?.eligible ||
      enrolled ||
      !status?.capacityAvailable ||
      assignedBot
    )
      return;
    requested.current = true;
    analytics.track("Steambot - Request Assignment", {});
    requestAssignment();
  }, [
    status?.eligible,
    status?.capacityAvailable,
    enrolled,
    assignedBot,
    requestAssignment,
  ]);

  // Without this, the not-linked state flashes before the status loads.
  if (loading && !status) return null;

  const eligible = Boolean(status?.eligible);
  const full =
    eligible && !enrolled && !assignedBot && !status?.capacityAvailable;

  const track = (via: "web" | "steam") => {
    analytics.track("Steambot - Open Bot Profile", { via });
    setOpened(true);
  };

  // Browsers can't tell us whether steam:// has a handler. If Steam opens, the
  // window blurs (or hides) right away; if nothing happens, fall back to the web
  // profile. Kept under a second so the new tab still counts as the click's
  // popup (Safari allows about 1s).
  const openInSteam = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!assignedBot) return;
    event.preventDefault();
    track("steam");

    let handled = false;
    const markHandled = () => {
      handled = true;
    };
    window.addEventListener("blur", markHandled, { once: true });
    document.addEventListener("visibilitychange", markHandled, { once: true });

    window.location.href = `steam://url/SteamIDPage/${assignedBot.steamId64}`;

    window.setTimeout(() => {
      window.removeEventListener("blur", markHandled);
      document.removeEventListener("visibilitychange", markHandled);
      if (handled) return;
      analytics.track("Steambot - Steam Client Fallback", {});
      window.open(assignedBot.profileUrl, "_blank", "noopener,noreferrer");
    }, 800);
  };

  // kanye enrolls any plan once Steam is linked, so that's the only gap.
  const [label, tone]: [string, Tone] = enrolled
    ? ["Connected", "positive"]
    : !eligible
    ? ["Link your Steam account first", "muted"]
    : full
    ? ["All bots are full, check back soon", "muted"]
    : opened
    ? ["Waiting for your friend request", "waiting"]
    : ["Not connected", "alert"];

  const actions = (() => {
    if (enrolled || full) return null;
    if (!eligible)
      return (
        <PrimaryButton
          type="button"
          onClick={() => {
            analytics.track("Steambot - Link Steam Clicked", {});
            history.push(URLS.CONNECTED_ACCOUNTS);
          }}
        >
          Link Steam
        </PrimaryButton>
      );
    if (!assignedBot)
      return (
        <PrimaryButton
          type="button"
          aria-disabled={assigning}
          onClick={() => !assigning && requestAssignment()}
        >
          {assigning ? "Finding your bot…" : "Try again"}
        </PrimaryButton>
      );
    return (
      <>
        <SecondaryButton
          href={assignedBot.profileUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track("web")}
        >
          Add via web
        </SecondaryButton>
        {/* Opens the profile in the Steam client, or the web if it can't. */}
        <PrimaryLink
          href={`steam://url/SteamIDPage/${assignedBot.steamId64}`}
          onClick={openInSteam}
        >
          <FaSteam aria-hidden="true" />
          Add to Steam
        </PrimaryLink>
      </>
    );
  })();

  return (
    <>
      <Row aria-label="Allstar Steam bot">
        <Identity>
          <img src={botAvatar} alt="" />
          <span>
            <Name>Allstar Steam bot</Name>
            <Status $tone={tone} role="status">
              {label}
            </Status>
          </span>
        </Identity>
        {actions && <Actions>{actions}</Actions>}
      </Row>
      {enrolled && !allstarUser?.user?.autoclip && (
        <Footnote>
          Want clips made automatically when we detect your match?{" "}
          <TextButton
            type="button"
            onClick={() => {
              analytics.track("Steambot - Open Clip Settings", {});
              openClipSettings();
            }}
          >
            Turn on Autocapture
          </TextButton>
        </Footnote>
      )}
    </>
  );
};
